import {
  Badge,
  Checkbox,
  Field,
  humanizedMarketplaceName,
  humanizedProviderName,
  Input,
  Radio,
  RadioInputs,
} from "@deligoo/ui"
import { isNumber } from "lodash"
import { FormEvent } from "react"
import { useFormContext, useFormState, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import NumberFormat from "react-number-format"

import { useAppSelector } from "@/App"
import { Card } from "@/components/Card"
import { PackageCard } from "@/components/PackageCard"
import { selectOrderById } from "@/store/orders"
import { Order } from "@/types"
import { formatPrice, OrderUtils, PAYMENT_FORMS, TRANSPORT_TYPES } from "@/utils"

import cls from "../EditOrderPanel.module.scss"
import { StateSelect } from "./StateSelect"

export const Details = ({ orderId }: { orderId: Order["id"] }) => {
  const order = useAppSelector(selectOrderById(orderId))

  const { setValue, register } = useFormContext()

  const { errors } = useFormState()

  const price_subunit = useWatch({
    name: "price_subunit",
  })

  const price_delivery_subunit = useWatch({
    name: "price_delivery_subunit",
  })

  const packages = useWatch({ name: "packages", defaultValue: order?.packages })

  const { t } = useTranslation()

  if (!order) return null

  const humanizedProvider = order.marketplace_kind
    ? humanizedMarketplaceName[order.marketplace_kind]
    : humanizedProviderName[order.provider]

  function formatInputPrice(e: FormEvent<HTMLInputElement>) {
    e.currentTarget.value = formatPrice(e.currentTarget.value)
  }

  function setPriceInputValue(input: "price_subunit" | "price_delivery_subunit", value: string | undefined) {
    const valueToSet = value ? Number(value) * 100 : undefined

    setValue(input, valueToSet, { shouldValidate: true })
  }

  return (
    <>
      <Card.Section>
        <Field label="Formy płatności" name="payment_form">
          <RadioInputs className="h200">
            {PAYMENT_FORMS.map((paymentForm) => (
              <Radio
                id={paymentForm}
                value={paymentForm}
                key={paymentForm}
                {...register("payment_form", { required: "Wybierz formę płatności" })}
              >
                {t(`orders:edit.${paymentForm}`)}
              </Radio>
            ))}
          </RadioInputs>
        </Field>

        <input {...register("price_subunit", { required: "Podaj kwotę" })} type="hidden" />

        <input {...register("price_delivery_subunit", { required: "Podaj kwotę" })} type="hidden" />

        <div className={cls.detailsCosts}>
          <Field name="price" label="Kwota zamówienia" error={errors.price_subunit?.message}>
            <NumberFormat
              id="price"
              value={formatPrice(price_subunit, { subunit: true })}
              onBlur={formatInputPrice}
              onChange={(e: FormEvent<HTMLInputElement>) => setPriceInputValue("price_subunit", e.currentTarget.value)}
              customInput={Input}
              decimalScale={2}
              isNumericString
              selectOnFocus
              plain
            />
          </Field>

          <Field name="price_delivery" label="Kwota dowozu" error={errors.price_delivery_subunit?.message}>
            <NumberFormat
              id="price_delivery"
              value={formatPrice(price_delivery_subunit, { subunit: true })}
              onBlur={formatInputPrice}
              onChange={(e: FormEvent<HTMLInputElement>) =>
                setPriceInputValue("price_delivery_subunit", e.currentTarget.value)
              }
              customInput={Input}
              decimalScale={2}
              isNumericString
              selectOnFocus
              plain
            />
          </Field>

          <div>
            <div className="text-muted">Suma</div>
            <div className="h300 mt-5">
              {isNumber(price_subunit) && isNumber(price_delivery_subunit)
                ? `${formatPrice(price_subunit + price_delivery_subunit, { subunit: true })} zł`
                : "-"}
            </div>
          </div>

          <div>
            <div className="text-muted">Status płatności</div>

            {order.payment_status ? (
              <Badge className="mt-3">{t(`orders:payment_status.${order.payment_status}`)}</Badge>
            ) : (
              <div className="mt-6">–</div>
            )}
          </div>
        </div>
      </Card.Section>

      <Card.Section className={cls.transportTypes}>
        <Field label="Dozwolone środki transportu" name="transport_types" error={errors.transport_types?.message}>
          <RadioInputs className="h200">
            {TRANSPORT_TYPES.map((transportType) => (
              <Checkbox
                id={transportType}
                value={transportType}
                key={transportType}
                {...register("transport_types", { required: "Wybierz formę transportu" })}
              >
                {t(`drivers:${transportType}`)}
              </Checkbox>
            ))}
          </RadioInputs>
        </Field>

        <div>
          <div className="text-muted mb-2">Transport kuriera</div>
          <div className="h300">{order.driver ? t(`drivers:${order.driver.transport_type}`) : "-"}</div>
        </div>
      </Card.Section>

      <Card.Section className={cls.packages}>
        {OrderUtils.getInterfacePackageTypes(order.client_interface_type).map((packageType) => {
          const inputName = `packages.${packageType}` as `${string}.${string}`

          return (
            <PackageCard
              type={packageType}
              value={Number(packages[packageType] || 0)}
              setValue={(value: number) => setValue(inputName, value)}
              inputProps={{ ...register(inputName) }}
              className={cls.package}
              key={packageType}
            />
          )
        })}
      </Card.Section>

      {order.pickup && (
        <Card.Section className="d-flex justify-between">
          <div className="h300">
            <div className="font-weight-bold mb-4">Odbiór</div>

            {order.pickup.name}
            <div className="text-muted">{order.pickup.address}</div>
            <div className="text-primary mt-3">{order.pickup.phone}</div>
          </div>

          <StateSelect name="pickup_attributes.state" defaultValue={order.pickup.state} />
        </Card.Section>
      )}

      <Card.Section className="h300">
        <dl className={cls.detailsList}>
          <div className={cls.detailsListItem}>
            <dt>Źródło</dt>
            <dd>{humanizedProvider || order.provider}</dd>
          </div>

          {order.order_authorization_type && (
            <div className={cls.detailsListItem}>
              <dt>Autoryzacja</dt>
              <dd>{t(`orders:${order.order_authorization_type}`)}</dd>
            </div>
          )}
        </dl>
      </Card.Section>
    </>
  )
}
